import * as CSS from "csstype";
import { ReactElement, useEffect, useMemo } from "react";

import {
  Button,
  Flex,
  Hide,
  IconButton,
  Show,
  Stack,
  Text,
} from "@chakra-ui/react";
import { HiOutlineMapPin } from "react-icons/hi2";

import { DeliveryModel } from "api";
import BackToTopFAB from "components/BackToTopFAB";
import { ComponentTabProps } from "components/Documents/documentsTabs/documentsTabPanels/DocumentsPageTabs";
import DocumentsEmptyState from "components/DocumentsEmptyState";
import FetchMoreWhenInView from "components/FetchMoreWhenInView";
import LoadingSpinner from "components/LoadingSpinner";
import {
  DOCUMENTS_BACK_TO_TOP_MIN_LINES,
  MobileSortableCardTable,
  SortableCardTable,
} from "components/SortableTable";
import DateColumn from "components/tableColumns/DateColumn/DateColumn";
import FileDownloadLink from "components/tableColumns/FileDownloadLink";
import { ActiveSort, useRequestParams } from "contexts/RequestParamsProvider";
import useDeliveries from "hooks/api/documents/useDeliveries";
import useDeliveryFiles from "hooks/api/documents/useDeliveryFiles";
import useSelectedCustomerCode from "hooks/useSelectedCustomerCode";
import useToggleRows from "hooks/useToggleRows";
import { iconSize } from "icons";
import { DEFAULT_EMPTY_VALUE, formatDate } from "util/statHelpers";
import {
  MOBILE_BREAKPOINT_SIZE,
  mobileStyleBreakpoint,
} from "util/styleHelpers";
import { deliveriesColumns } from "./deliveriesColumns";

const LOCATION_DIRECTION = mobileStyleBreakpoint<CSS.Property.FlexDirection>(
  "row",
  "column"
);

const DeliveriesTable = ({ search }: ComponentTabProps): ReactElement => {
  const selectedCustomerCodes = useSelectedCustomerCode();
  const { filters, activeSort } = useRequestParams();
  const { toggleRow, unToggleRow, toggledRows, clearRows } = useToggleRows();
  const { mutateAsync } = useDeliveryFiles();

  const {
    data,
    isLoading: deliveriesLoading,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  } = useDeliveries(activeSort as ActiveSort<DeliveryModel>, {
    cardCodes: selectedCustomerCodes,
    searchString: search,
    ...filters,
  });
  const deliveries = useMemo(
    () =>
      data?.pages?.map(({ deliveries: _deliveries }) => _deliveries).flat() ??
      [],
    [data]
  );

  useEffect(() => {
    clearRows();
    return () => {
      clearRows();
    };
  }, [clearRows]);

  const deliveriesTableEntries = useMemo(
    () =>
      deliveries?.map((value) => ({
        ...value,
        releaseNumber: (
          <FileDownloadLink
            label={value.releaseNumber}
            onFetchFiles={mutateAsync}
            docEntry={value.docEntry}
          />
        ),
        quantityFormatted: value.isReturn
          ? `(${value.quantityFormatted}) ${value.unitOfMeasure}`
          : `${value.quantityFormatted} ${value.unitOfMeasure}`,
        createDate: <DateColumn date={value.createDate} />,
        docDueDate: <DateColumn date={value.docDueDate} />,
        expandRow:
          value.clm?.id !== undefined ? (
            <IconButton
              variant="link"
              boxSize={iconSize}
              aria-label="expand row"
              icon={<HiOutlineMapPin />}
              onClick={() =>
                value.clm?.id !== undefined && toggleRow(value.clm.id)
              }
              minW="unset"
            />
          ) : undefined,
        expansionRow: [
          { label: "Carrier Name", value: value.clm?.carrierName },
          {
            label: "Date and Time",
            value: value.clm?.date ? (
              <Stack spacing={0}>
                <Text textStyle="body2" color="grey.700">
                  {formatDate(value.clm.date)}
                </Text>
                <Text textStyle="body2" color="grey.700">
                  {value.clm?.date.toLocaleTimeString("en-us", {
                    timeStyle: "short",
                  })}
                </Text>
              </Stack>
            ) : (
              DEFAULT_EMPTY_VALUE
            ),
          },
          { label: "Details", mobileSpan: 2, value: value.clm?.details },
          {
            label: "Location",
            mobileSpan: 2,
            value: (
              <Stack spacing={0} flexDirection={LOCATION_DIRECTION}>
                <Text textStyle="body2" color="grey.700" mr={1}>
                  {value.clm?.currentCity},
                </Text>
                <Text textStyle="body2" color="grey.700">
                  {value.clm?.currentState}
                </Text>
              </Stack>
            ),
          },
          {
            label: "Original ETA",
            value: <DateColumn date={value.clm?.originalEta} />,
          },
          {
            label: "Updated ETA",
            value: <DateColumn date={value.clm?.updatedEta} />,
          },
          {
            mobileSpan: 2,
            mobileAlign: "center",
            value: (
              <Flex justify="flex-end">
                <Button
                  variant="link"
                  onClick={() => unToggleRow(value.clm?.id ?? "")}
                >
                  Close
                </Button>
              </Flex>
            ),
          },
        ],
        rowExpanded: value.clm?.id
          ? toggledRows.indexOf(value.clm.id) >= 0
          : false,
      })) ?? [],
    [unToggleRow, deliveries, toggledRows, mutateAsync, toggleRow]
  );

  if (deliveriesLoading) {
    return <LoadingSpinner />;
  }

  return deliveries.length > 0 ? (
    <BackToTopFAB
      isVisible={
        deliveriesTableEntries.length > DOCUMENTS_BACK_TO_TOP_MIN_LINES
      }
    >
      <Hide below={MOBILE_BREAKPOINT_SIZE}>
        <SortableCardTable
          values={deliveriesTableEntries}
          columns={deliveriesColumns}
          hasExpansionRow={true}
        />
      </Hide>
      <Show below={MOBILE_BREAKPOINT_SIZE}>
        <MobileSortableCardTable
          values={deliveriesTableEntries ?? []}
          columns={deliveriesColumns}
          hasExpansionRow={true}
        />
      </Show>
      <FetchMoreWhenInView
        fetchNextPage={fetchNextPage}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        key={`${data?.pages.length}`}
      />
    </BackToTopFAB>
  ) : (
    <DocumentsEmptyState />
  );
};

export default DeliveriesTable;
